import { useState, useEffect } from "react";
import { studioAPI, tryParseJSON, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { EPISODE } from "@/constants/testIds";
import { toast } from "sonner";
import { Wand2, ArrowRight, Copy, Download } from "lucide-react";
import AskDirector from "@/components/AskDirector";

const TONES = ["humor", "suspense", "wholesome", "dramatic", "educational", "cartoon"];

export default function EpisodeGenerator() {
    const { lang, activeProjectId, projects } = useApp();
    const [channel, setChannel] = useState("");
    const [character, setCharacter] = useState("");
    const [idea, setIdea] = useState("");
    const [tone, setTone] = useState("humor");
    const [busy, setBusy] = useState(false);
    const [raw, setRaw] = useState("");
    const [episodeId, setEpisodeId] = useState(null);
    const parsed = tryParseJSON(raw);

    // Auto-fill from the Director's inline action
    useEffect(() => {
        const handler = (e) => {
            const p = e.detail || {};
            if (p.idea) setIdea(p.idea);
            if (p.tone) setTone(p.tone);
            const active = projects.find((x) => x.id === activeProjectId);
            if (active) {
                if (!channel) setChannel(active.name);
                if (!character && active.character) setCharacter(active.character);
            }
            toast.success(lang === "fr" ? "Idée transmise par le Directeur" : "Idea filled by the Director");
        };
        window.addEventListener("sf:director-fill-episode", handler);
        return () => window.removeEventListener("sf:director-fill-episode", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProjectId, projects, channel, character]);

    const run = async () => {
        if (!idea || !channel) { toast.error(lang === "fr" ? "Renseigne la chaîne + idée" : "Fill channel + idea"); return; }
        setBusy(true); setRaw(""); setEpisodeId(null);
        try {
            const res = await studioAPI.episode({ channel_name: channel, character, idea, tone, language: lang, project_id: activeProjectId });
            setRaw(res.result);
            setEpisodeId(res.id);
            window.dispatchEvent(new Event("sf:quota-changed"));
        } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    const copyAll = () => { navigator.clipboard.writeText(raw); toast.success("Copié"); };

    const downloadBundle = async () => {
        if (!episodeId) return;
        const token = localStorage.getItem("sf_token");
        try {
            const resp = await fetch(`${API}/export/episode/${episodeId}`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `shorts-factory-${episodeId.slice(0, 8)}.txt`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(lang === "fr" ? "Bundle téléchargé" : "Bundle downloaded");
        } catch { toast.error("Download error"); }
    };

    return (
        <div data-testid={EPISODE.container} className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-4">
                <div>
                    <div className="overline">02 · Episode</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Générer un épisode" : "Generate an episode"}</h2>
                    <div className="mt-2">
                        <AskDirector
                            testid="episode-ask-director"
                            prompt={lang === "fr" ? "Peux-tu améliorer mon hook et rendre ce Short plus addictif ?" : lang === "es" ? "¿Puedes mejorar mi hook y hacer este Short más adictivo?" : "Can you improve my hook and make this Short more addictive?"}
                            label={lang === "fr" ? "Besoin d'améliorer ton hook ?" : lang === "es" ? "¿Necesitas mejorar tu hook?" : "Need to improve your hook?"}
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Nom de chaîne" : "Channel name"}</label>
                    <input data-testid={EPISODE.channel} value={channel} onChange={(e) => setChannel(e.target.value)}
                        placeholder="Gaston le chat" className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Personnage récurrent" : "Recurring character"}</label>
                    <input data-testid={EPISODE.character} value={character} onChange={(e) => setCharacter(e.target.value)}
                        placeholder="Gaston, gros chat roux paresseux" className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Idée d'épisode" : "Episode idea"}</label>
                    <textarea data-testid={EPISODE.idea} value={idea} onChange={(e) => setIdea(e.target.value)} rows={4}
                        placeholder={lang === "fr" ? "Gaston passe son entretien d'embauche…" : "Gaston does his job interview…"}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00] resize-none" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Ton" : "Tone"}</label>
                    <select data-testid={EPISODE.tone} value={tone} onChange={(e) => setTone(e.target.value)}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]">
                        {TONES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <button data-testid={EPISODE.generateBtn} onClick={run} disabled={busy}
                    className="w-full flame-gradient text-black font-semibold py-3 rounded flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60">
                    {busy ? "···" : <>{lang === "fr" ? "Générer l'épisode" : "Generate episode"} <ArrowRight className="w-4 h-4" /></>}
                </button>
            </div>

            <div data-testid={EPISODE.result} className="lg:col-span-3 rounded border border-[#253046] bg-[#0B111B] p-5 min-h-[500px]">
                {!raw && !busy && (
                    <div className="h-full flex flex-col items-center justify-center text-center py-20">
                        <Wand2 className="w-10 h-10 text-slate-700 mb-3" />
                        <p className="text-sm text-slate-500">{lang === "fr" ? "Ton épisode complet apparaîtra ici — hook, script, storyboard, hashtags." : "Your full episode — hook, script, storyboard, hashtags — will appear here."}</p>
                    </div>
                )}
                {busy && <div className="h-full flex items-center justify-center text-slate-400 font-mono-sf text-xs">WRITING EPISODE ···</div>}
                {parsed && (
                    <div className="space-y-5">
                        <div className="flex justify-between items-start">
                            <div>
                                <div className="overline">Title</div>
                                <div className="font-display text-2xl font-black uppercase mt-1">{parsed.title}</div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={copyAll} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 border border-[#253046] px-2 py-1 rounded"><Copy className="w-3 h-3" /> All</button>
                                {episodeId && (
                                    <button
                                        onClick={downloadBundle}
                                        data-testid="episode-export-btn"
                                        className="text-xs font-semibold text-black bg-[#FF4D00] hover:brightness-110 flex items-center gap-1 px-2.5 py-1 rounded kinetic"
                                    >
                                        <Download className="w-3 h-3" /> {lang === "fr" ? "Bundle" : "Bundle"}
                                    </button>
                                )}
                            </div>
                        </div>

                        {parsed.hook && (
                            <div>
                                <div className="overline">Hooks (3 options)</div>
                                <div className="mt-2 space-y-1.5">
                                    {(Array.isArray(parsed.hook) ? parsed.hook : [parsed.hook]).map((h, i) => (
                                        <div key={i} className="bg-[#161D2A] border-l-2 border-[#FF4D00] rounded-r p-3 text-sm">{h}</div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {parsed.script && (
                            <div>
                                <div className="overline">Script</div>
                                <pre className="mt-2 bg-[#161D2A] border border-[#253046] rounded p-3 text-sm text-slate-200 whitespace-pre-wrap">
                                    {typeof parsed.script === "string" ? parsed.script : JSON.stringify(parsed.script, null, 2)}
                                </pre>
                            </div>
                        )}

                        {parsed.storyboard && (
                            <div>
                                <div className="overline">Storyboard · {parsed.storyboard.length} shots</div>
                                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                                    {parsed.storyboard.map((s, i) => (
                                        <div key={i} className="bg-[#161D2A] border border-[#253046] rounded p-3">
                                            <div className="font-mono-sf text-[10px] text-[#FF4D00]">SHOT {String(i + 1).padStart(2, "0")}</div>
                                            <div className="text-xs text-slate-300 mt-1">{s.visual_prompt}</div>
                                            {s.on_screen_text && <div className="text-xs text-[#FFB800] mt-2 font-medium">« {s.on_screen_text} »</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid sm:grid-cols-2 gap-3">
                            {parsed.voice_over && <div className="bg-[#161D2A] border border-[#253046] rounded p-3"><div className="overline">Voice-over</div><div className="text-sm mt-1 text-slate-300">{parsed.voice_over}</div></div>}
                            {parsed.music_mood && <div className="bg-[#161D2A] border border-[#253046] rounded p-3"><div className="overline">Music</div><div className="text-sm mt-1 text-slate-300">{parsed.music_mood}</div></div>}
                        </div>

                        {parsed.hashtags && (
                            <div>
                                <div className="overline">Hashtags</div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {parsed.hashtags.map((h, i) => <span key={i} className="text-xs px-2 py-1 rounded bg-[#0099FF]/10 border border-[#0099FF]/30 text-[#0099FF]">#{h.replace(/^#/, "")}</span>)}
                                </div>
                            </div>
                        )}
                        {parsed.cta && (
                            <div className="bg-gradient-to-r from-[#FF4D00]/20 to-transparent border-l-2 border-[#FF4D00] p-3 rounded-r">
                                <div className="overline">CTA</div>
                                <div className="text-sm mt-1 text-white font-medium">{parsed.cta}</div>
                            </div>
                        )}
                    </div>
                )}
                {raw && !parsed && <pre className="text-xs text-slate-300 whitespace-pre-wrap">{raw}</pre>}
            </div>
        </div>
    );
}
